import type { ChatCredentials } from "@shared/types/user";
import PubNub from "pubnub";
import React from "react";

// Refresh ~5 min before JWT expiry. The BE keeps a 10-min refresh window
// of its own, so any call inside the last 5 min is guaranteed to mint a fresh token.
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

type ParsedToken = { ttl?: number; timestamp?: number };

export const getTokenExpiryMs = (
  chat: PubNub | null,
  token: string | null | undefined,
): number | null => {
  if (!chat || !token) return null;

  let parsed: ParsedToken | undefined;
  try {
    parsed = chat.parseToken(token) as ParsedToken | undefined;
  } catch (error) {
    // `parseToken` base64-decodes the PAM token and runs it through PubNub's
    // CBOR decoder, which throws a bare string ("Remaining bytes") — not an
    // Error — whenever the token isn't decodable. This runs inside a `useMemo`
    // in <App/>, so an unguarded throw takes down every route in the app with
    // a React Router render error, which is a wildly disproportionate response
    // to not knowing when a token expires.
    //
    // Degrade instead: a null expiry only costs the scheduled pre-emptive
    // refresh. The visibility/online re-checks no-op, and the PubNub status
    // listener below still refetches on PNAccessDeniedCategory — so an
    // unparseable token that the server also rejects is still replaced.
    console.error("[chat] parseToken failed", {
      tokenLength: token.length,
      error,
    });

    return null;
  }

  if (!parsed?.ttl || !parsed?.timestamp) return null;
  // PubNub: timestamp is unix seconds, ttl is minutes.
  return parsed.timestamp * 1000 + parsed.ttl * 60 * 1000;
};

export const isTokenStale = (
  expiryMs: number | null,
  now: number = Date.now(),
): boolean => {
  if (!expiryMs) return false;
  return now + REFRESH_BEFORE_EXPIRY_MS >= expiryMs;
};

type Args = {
  chat: PubNub | null;
  credentials: Pick<ChatCredentials, "token"> | null;
  refetch: () => unknown;
};

export const useChatTokenRefresh = ({ chat, credentials, refetch }: Args) => {
  const refetchRef = React.useRef(refetch);
  refetchRef.current = refetch;

  const expiryMs = React.useMemo(
    () => getTokenExpiryMs(chat, credentials?.token),
    [chat, credentials?.token],
  );

  const refreshIfStale = React.useCallback(() => {
    if (isTokenStale(expiryMs)) refetchRef.current();
  }, [expiryMs]);

  // Foreground: schedule a precise refresh just before expiry.
  React.useEffect(() => {
    if (!expiryMs) return;
    const delay = expiryMs - REFRESH_BEFORE_EXPIRY_MS - Date.now();
    if (delay <= 0) {
      refetchRef.current();
      return;
    }
    const timer = setTimeout(() => refetchRef.current(), delay);
    return () => clearTimeout(timer);
  }, [expiryMs]);

  // Backgrounded tabs and laptop sleep can pause/throttle setTimeout, so we
  // also re-check expiry whenever the tab becomes visible or the network
  // comes back. These events are reliably fired on re-engagement.
  React.useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshIfStale();
    };
    const onOnline = () => refreshIfStale();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", onOnline);
    };
  }, [refreshIfStale]);

  // PubNub-driven: react to auth denials (token already invalid server-side)
  // and post-reconnect (token may have expired during the disconnection).
  React.useEffect(() => {
    if (!chat) return;
    const listener = {
      status: ({ category }: { category?: string }) => {
        if (category === "PNAccessDeniedCategory") {
          refetchRef.current();
          return;
        }
        if (
          category === "PNReconnectedCategory" ||
          category === "PNNetworkUpCategory"
        ) {
          refreshIfStale();
        }
      },
    };
    chat.addListener(listener);
    return () => chat.removeListener(listener);
  }, [chat, refreshIfStale]);
};

export default useChatTokenRefresh;
